import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DocumentSelectionInfo } from '@app/modules/communications/store/communications';
import { TableColumnsService } from '@app/modules/cop/services/table/table-columns-service';
import { DataTableServerComponent } from '@app/modules/core/components/data-table-server/data-table.component';
import { DatasetApiService } from '@app/modules/core/services/dataset-api.service';
import { GridHelperService } from '@app/modules/core/services/grid-helper.service';
import { Dataset, DocumentQueryParams, FilterGroup, FilterGroupType } from '@app/modules/core/store/models';
import { DataApiService } from '@app/modules/management/store/data/data-api.service';
import { TranslateService } from '@ngx-translate/core';
import { GetRowIdParams, IServerSideDatasource, RowSelectedEvent, ValueFormatterParams } from 'ag-grid-enterprise';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'topo-comms-related-assets-recipients-selection-list',
  templateUrl: './related-assets-recipients-selection-list.component.html',
  styleUrls: ['./related-assets-recipients-selection-list.component.scss'],
  standalone: false
})
export class RelatedAssetsRecipientsSelectionListComponent {
  @Input() set activeContext(value: RelatedAssetsRecipientsSelectionContext) {
    this.activatedContext = value;

    if (!!this.activatedContext) {
      setTimeout(() => {
        this.dataTable?.groupRows(false);
        this.dataTable?.resetFilter();
      }, 200);

      this.loadData();
    }
  }

  activatedContext: RelatedAssetsRecipientsSelectionContext;

  @Input() getSelectedUIds: (uId: string) => DocumentSelectionInfo = null;

  @Output() recipientsSelected = new EventEmitter<RelatedRecipientSelectionInfo>();
  @Output() recipientsSelectionClosed = new EventEmitter();

  @ViewChild('table') dataTable: DataTableServerComponent;

  dialogLabel: string;
  headerLabel: string;
  headerIcon: string;
  datasource: any;
  columnDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  searchFilterGroup: FilterGroup;
  dataset: Dataset;
  isDirty: boolean;
  selectAll: boolean;
  selectedUIds: string[];
  tableTabs: any[] = [];

  destroy$: Subject<void> = new Subject<void>();

  tableWidth = '70vw';
  tableHeight = '60vh';
  private readonly gridPageSize = 10;

  constructor(
    private gridHelper: GridHelperService,
    private translateService: TranslateService,
    private tableColumnsService: TableColumnsService,
    private dataApiService: DataApiService,
    private datasetApiService: DatasetApiService,
    private userContextService: UserContextService
  ) {}

  ngOnInit(): void {
    this.datasource = this.getServerSideDatasource();
    this.dialogLabel = this.translateService.instant('COMMUNICATIONS.RECIPIENT-DETAILS');
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: gridParams => {
        if (!!!this.dataset) {
          gridParams.success({
            rowData: [],
            rowCount: 0
          });

          return;
        }

        if (!!this.activatedContext) {
          var documentQuery = this.buildDocumentQuery(gridParams);

          // call the data API to get the documents
          this.dataApiService
            .getDocuments(this.dataset.uId, documentQuery)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              resp => {
                const response = this.gridHelper.queryResultToGridData(resp);
                this.totalCount = response?.rowCount;

                gridParams.success({
                  rowData: response?.rowData,
                  rowCount: response?.rowCount
                });
              },
              // err is never used, why?
              err => gridParams.fail()
            );
        }
      }
    };
  }

  private buildDocumentQuery(gridParams) {
    const combinedFilterGroup: FilterGroup = {
      filterGroups: [],
      filters: [],
      groupType: FilterGroupType.And
    };
    if (!!this.searchFilterGroup) {
      combinedFilterGroup.filterGroups.push(this.searchFilterGroup);
    }
    if (!!this.activatedContext.targetFilterGroup) {
      combinedFilterGroup.filterGroups.push(this.activatedContext.targetFilterGroup);
    }
    var documentQuery = this.buildGridParamsQuery(gridParams);
    if (!!documentQuery.filterGroup) {
      combinedFilterGroup.filterGroups.push(documentQuery.filterGroup);
    }
    documentQuery.filterGroup = combinedFilterGroup;
    return documentQuery;
  }

  private buildGridParamsQuery(gridParams): DocumentQueryParams {
    const requestParams = this.gridHelper.gridParamsToRequestParams(gridParams.request);
    let columnFilter = this.tableColumnsService.getColumnFilterFromGrid(gridParams?.request.filterModel);
    return {
      pagination: {
        skip: requestParams.skip,
        take: requestParams.take
      },
      sort: requestParams.sort,
      filterGroup: columnFilter
    } as DocumentQueryParams;
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  get allSelected(): boolean {
    return this.selectAll === true && this.selectedUIds.length === 0;
  }

  get hasNotSelected(): boolean {
    return !this.allSelected && this.selectedUIds?.length > 0;
  }

  onSelectUnselectAll(): void {
    if (this.hasNotSelected) {
      this.onUnSelectAll();
    } else {
      if (this.allSelected === true) {
        this.onUnSelectAll();
      } else {
        this.onSelectAll();
      }
    }
  }

  onRowSelected(event: RowSelectedEvent): void {
    if (!!!this.selectedUIds) {
      this.selectedUIds = [];
    }

    const isSelected = event.node?.isSelected();

    var uid = event.node?.data?.uid;
    // if selected
    if (isSelected === true) {
      // if not select all, add it
      if (this.selectAll !== true) {
        if (!this.selectedUIds.includes(uid)) {
          this.selectedUIds.push(uid);
          this.isDirty = true;
        }
      }
      // if select all, remove it
      else {
        const index = this.selectedUIds.indexOf(uid);
        if (index >= 0) {
          this.selectedUIds.splice(index, 1);
          this.isDirty = true;
        }
      }
      // else if unselected
    } else {
      if (this.selectAll === true) {
        if (!this.selectedUIds.includes(uid)) {
          this.selectedUIds.push(uid);
          this.isDirty = true;
        }
      } else {
        const index = this.selectedUIds.indexOf(uid);
        if (index >= 0) {
          this.selectedUIds.splice(index, 1);
          this.isDirty = true;
        }
      }
    }

    this.recipientsChanged();
  }

  getSelectedCount(): number {
    const selected = this.selectedUIds?.length ?? 0;
    return this.selectAll == true ? this.totalCount - selected : selected;
  }

  private buildTableColumns(): void {
    if (!!this.dataset) {
      this.columnDefs = this.tableColumnsService.getDatasetColumns(
        this.dataset,
        this.userContextService.getUserContext().organizationUId,
        false,
        true
      );
    }
    // add select column
    const column = {
      colId: '_selected',
      headerClass: 'ag-header-no-label',
      sortable: true,
      checkboxSelection: false,
      resizable: false,
      pinned: 'left',
      menuTabs: [],
      width: 40,
      maxWidth: 60,
      minWidth: 60
    };

    this.columnDefs.unshift(column);
  }

  // format the text for the table, this looks generic, could be refactored
  formatTextForTable(params: ValueFormatterParams) {
    const text = params.value;
    if (text) {
      return text.replace(/(\r\n|\n|\r)/gm, '');
    }
    return text;
  }

  private loadData(): void {
    this.isDirty = false;

    // set table height
    if (this.tableTabs.length > 0) {
      this.tableHeight = 'calc(80vh - 388px)';
    } else {
      this.tableHeight = 'calc(80vh - 348px)';
    }

    const datasetUId = this.activatedContext.datasetUId;

    // get the dataset information (not documents)
    this.datasetApiService
      .getDataset(datasetUId, { includeWorkflow: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe(dataset => {
        if (!!dataset) {
          this.dataset = dataset;
          this.buildTableColumns();
          const selectInfo = this.getSelectedUIds(this.activatedContext.uId);
          this.selectAll = selectInfo.selectAll;
          this.selectedUIds = cloneDeep(selectInfo.documentUIds);
          // reset skip/take
          this.skip = 0;
          this.take = this.gridPageSize;

          this.headerLabel = this.translateService.instant('COMMUNICATIONS.DETAIL-RELATED-ASSETS', { name: this.dataset.name });

          this.dataTable?.refreshGrid();
        }
      });
  }

  close(): void {
    this.recipientsSelectionClosed.emit();
  }

  cancelClick(): void {
    this.close();
  }

  private recipientsChanged() {
    const selections = {
      uId: this.activatedContext.uId,
      selectInfo: {
        selectAll: this.selectAll,
        documentUIds: []
      }
    } as RelatedRecipientSelectionInfo;

    this.selectedUIds?.forEach(s => {
      selections.selectInfo.documentUIds.push(s);
    });

    this.recipientsSelected.emit(selections);
    this.dataTable?.refreshGrid();

    this.isDirty = false;
  }

  // search filter from the grid?
  onSearchFilterChanged(filterGroup: FilterGroup): void {
    this.searchFilterGroup = filterGroup;
  }

  // callback from grid when loaded
  // populate selection state
  // this gets called a lot of times per grid refresh
  onGridDataLoaded(): void {
    this.dataTable?.gridApi?.sizeColumnsToFit();

    if (!!!this.selectedUIds) {
      this.selectedUIds = [];
    }
    this.dataTable?.gridApi?.forEachNode(node => {
      if (!!node.data) {
        const selected = this.selectedUIds?.includes(node.data?.uid);
        if (this.selectAll === true) {
          node.setSelected(!selected, false, null);
        } else {
          node.setSelected(selected, false, null);
        }
      }
    });
  }

  // generic could be refactored
  private onSelectAll(): void {
    this.selectAll = true;
    this.selectedUIds = [];
    this.isDirty = true;
    this.recipientsChanged();
  }

  // generic could be refactored
  private onUnSelectAll(): void {
    this.selectAll = false;
    this.selectedUIds = [];
    this.isDirty = true;
    this.recipientsChanged();
  }

  // use uid as row id
  // generic, could be refactored
  getRowId = (params: GetRowIdParams): string => {
    return params.data.uid;
  };
}

export interface RelatedAssetsRecipientsSelectionContext {
  uId: string;
  datasetUId: string;
  targetFilterGroup?: FilterGroup;
}

export interface RelatedRecipientSelectionInfo {
  uId: string;
  selectInfo: DocumentSelectionInfo;
}
